import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

/**
 * Reviewing community membership applications.
 *
 * Membership works by application: a profile is created PENDING and is not listed
 * publicly until somebody approves it. Nothing could approve one from a screen, so the
 * community directory stays empty no matter how many people apply.
 */

export interface MemberProfile {
  id: string;
  slug: string;
  displayName: string;
  headline?: string | null;
  bio?: string | null;
  location?: string | null;
  avatarUrl?: string | null;
  showreelUrl?: string | null;
  primaryCraft?: string | null;
  primaryCraftLabel?: string | null;
  skills?: string[] | null;
  status: string;
  foundingMember: boolean;
  websiteUrl?: string | null;
  imdbUrl?: string | null;
  instagramUrl?: string | null;
  joinedAt?: string | null;
}

/** Decisions that change whether somebody is in the community. Mirrors the server. */
export const DECISIONS_NEEDING_A_NOTE = ['ACTIVE', 'ARCHIVED'];

export const MEMBER_STATUSES: { value: string; label: string; help: string }[] = [
  { value: 'ACTIVE', label: 'Approve', help: 'Listed in the public directory. The member is emailed. A note is required.' },
  { value: 'ARCHIVED', label: 'Remove', help: 'Removed from the directory. The member is emailed. A note is required.' },
  { value: 'HIDDEN', label: 'Hide', help: 'Not listed. No email - hiding is normally the member’s own choice.' },
  { value: 'PENDING', label: 'Back to pending', help: 'Returns to the review queue. No email.' },
];

function apiUrl(path: string): string {
  const base = (config.apiBaseUrl || '').replace(/\/+$/, '');
  return base.endsWith('/api') ? `${base}/community${path}` : `${base}/api/community${path}`;
}

async function unwrap<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) throw new Error(json?.message || `Request failed (${response.status})`);
  return (json?.data ?? json) as T;
}

export async function listMembers(status: string): Promise<MemberProfile[]> {
  const response = await unifiedAuth.fetch(
    apiUrl(`/admin/members?status=${encodeURIComponent(status)}&size=100`), { method: 'GET' });
  const payload = await unwrap<{ content?: MemberProfile[] } | MemberProfile[]>(response);
  return Array.isArray(payload) ? payload : payload?.content ?? [];
}

export async function decideMember(
  slug: string,
  status: string,
  notes: string,
  foundingMember?: boolean,
): Promise<MemberProfile> {
  const response = await unifiedAuth.fetch(apiUrl(`/admin/members/${encodeURIComponent(slug)}/status`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, notes, foundingMember }),
  });
  return unwrap<MemberProfile>(response);
}
